import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';
import { useRequestDialogStyles } from "./styles";
import useFetch from "../../../../hooks/useFetch";
import { toast } from "react-toastify";
import deleteIcon from "../../../../assets/images/delete_icon.png";
import { capitalizeWords } from "../../consts";

export const CourseRequestDialog = ({ open, setOpen }) => {
  const classes = useRequestDialogStyles();
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [grades, setGrades] = useState({});

  const [courses, setCourses] = useState([
    { id: Date.now(), category: "", course: "", grade: "", courseName: "" },
  ]);

  useFetch("/mentor/courses", {
    onSuccess: (data) => setCategories(data?.courseSection),
  });

  const { mutate: getCourses } = useFetch("/mentor/courses", {
    manual: true,
  });

  const { mutate: submitCourses, loading: sumbitCourseLoading } = useFetch(
    "/mentor/course/request",
    {
      method: "post",
      manual: true,
    }
  );

  const handleCategorySelected = (event, id) => {
    const value = event.target.value;
    setCourses((prev) =>
      prev.map((courseObj) =>
        courseObj.id === id
          ? {
              id: courseObj.id,
              category: value,
              course: "",
              grade: "",
              courseName: "",
            }
          : courseObj
      )
    );
    getCourses({
      params: { category: value },
      onSuccess: (data) => {
        if ("level" in data) {
          setGrades((prev) => ({
            ...prev,
            [value]: data?.level,
          }));
          setSubjects((prev) => ({ ...prev, [value]: null }));
        } else {
          setSubjects((prev) => ({
            ...prev,
            [value]: data?.subjectCategory,
          }));
        }
      },
    });
  };

  const handleAddCourse = () => {
    setCourses([
      ...courses,
      {
        id: Date.now(),
        category: "",
        course: "",
        grade: "",
        courseName: "",
      },
    ]);
  };

  const handleCourseChange = (event, id) => {
    const value = event.target.value;

    const course = courses.find((course) => course.id === id);
    setCourses((prev) =>
      prev.map((courseObj) =>
        courseObj.id === id ? { ...courseObj, course: value } : courseObj
      )
    );
    getCourses({
      params: { category: course.category, subject: value },
      onSuccess: (data) =>
        setGrades((prev) => ({
          ...prev,
          [course.category]: data?.level,
        })),
    });
  };

  const handleSubmit = () => {
    const isValid = courses.every(
      (course) => course.category.trim() !== "" && course.grade.trim() !== ""
    );

    if (!isValid) {
      toast.error("Please select all options!", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

    const finalCourses = courses.map((course) => {
      if (course.course) {
        return {
          courseName: capitalizeWords(course.course + " " + course.grade),
        };
      } else {
        return {
          courseName: capitalizeWords(course.category + " " + course.grade),
        };
      }
    });

    submitCourses({
      data: { courseData: finalCourses },
      onSuccess: () => {
        toast.success("Request submitted successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        handleClose();
      },
      onError: () =>
        toast.error("Could not submit course request! Please try again later", {
          position: toast.POSITION.TOP_RIGHT,
        }),
    });
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth="md"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">Request New Course</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {courses.map((course, index) => (
          <div key={course.id} className={classes.courseRow}>
            <div className={classes.formRow}>
              <div className={classes.formGroup}>
                <Typography className={classes.label}>
                  Category <span className={classes.requiredStar}>*</span>
                </Typography>
                <FormControl variant="outlined" className={classes.select}>
                  <Select
                    value={course.category || ""}
                    onChange={(e) => handleCategorySelected(e, course.id)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select category</em>
                    </MenuItem>
                    {(Array.isArray(categories) ? categories : []).map(
                      (category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </div>

              {subjects?.[course.category] !== null && (
                <div className={classes.formGroup}>
                  <Typography className={classes.label}>
                    Course <span className={classes.requiredStar}>*</span>
                  </Typography>
                  <FormControl variant="outlined" className={classes.select}>
                    <Select
                      value={course.course || ""}
                      onChange={(e) => handleCourseChange(e, course.id)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select course</em>
                      </MenuItem>
                      {subjects?.[course.category]?.map((courseOption) => (
                        <MenuItem key={courseOption} value={courseOption}>
                          {courseOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}

              {grades !== null && (
                <div className={classes.formGroup}>
                  <Typography className={classes.label}>
                    Grade <span className={classes.requiredStar}>*</span>
                  </Typography>
                  <FormControl variant="outlined" className={classes.select}>
                    <Select
                      value={course.grade || ""}
                      onChange={(e) => {
                        const grade = e.target.value;
                        const courseName =
                          e.nativeEvent.target.getAttribute("data-courseName");
                        setCourses((prev) =>
                          prev.map((courseObj) =>
                            courseObj.id === course.id
                              ? {
                                  ...courseObj,
                                  grade,
                                  courseName,
                                }
                              : courseObj
                          )
                        );
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select grade</em>
                      </MenuItem>
                      {grades?.[course.category]?.map((grade) => (
                        <MenuItem
                          key={grade.courseName}
                          value={grade.level}
                          data-courseName={grade.courseName}
                        >
                          {grade.level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
              <img
                src={deleteIcon}
                width="20px"
                height="20px"
                style={{
                  alignSelf: "center",
                  position: "relative",
                  top: "12px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setCourses((prev) =>
                    prev.filter((courseObj) => courseObj.id !== course.id)
                  )
                }
              />
            </div>
          </div>
        ))}

        <Button
          onClick={handleAddCourse}
          className={classes.addButton}
          startIcon={<ControlPointRoundedIcon />}
        >
          
          Add another course
        </Button>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className={classes.submitButton}
          disabled={sumbitCourseLoading}
        >
          {sumbitCourseLoading ? "submitting..." : "submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
